import React from 'react';
import { Organization } from '../data/Organization';
import CompanyIcon from '../resources/images/icons-company2.svg';
import FeedbackIcon from '../resources/images/icons-feedback.svg';
import ProfileIcon from '../resources/images/icons-user.svg';
import TeamIcon from '../resources/images/icons-users-1.svg';
import { canUseJobBuilder } from '../views/job-builder/jobBuilderAccess';

export const AppPages = {
  dashboard: {
    name: 'Dashboard',
    nameKey: 'navigation.dashboard',
    slug: 'dashboard',
    label: 'Dashboard',
    enabled: false,
    hasAccess: (user: any, organization: any) =>
      user &&
      !user.isMyKlayoUser &&
      (!organization || (organization && organization.status !== Organization.status.inactive)),
    developerOnly: true,
    ref: React.createRef(),
  },
  'my-klayo': {
    name: 'My Klayo',
    nameKey: 'navigation.myKlayo',
    slug: 'my-klayo',
    paths: ['/myklayo'],
    label: 'My Klayo',
    isDefault: (user: any) => !!user?.isMyKlayoUser,
    hasAccess: (_user: any, organization: any) => organization?.enabledFeatures?.MyKlayo !== false,
    ref: React.createRef(),
  },
  profile: {
    name: 'My profile',
    nameKey: 'navigation.myProfile',
    slug: 'profile',
    paths: ['/profile'],
    //getSlug: (user, organization) => user && !user.hasJob ? 'profile/competencies' : 'profile',
    label: 'My profile',
    isDefault: (user: any) => !user?.isMyKlayoUser,
    icon: <ProfileIcon />,
    hasAccess: (user: any, organization: any) =>
      user &&
      !user.isMyKlayoUser &&
      user.isFullAccess &&
      (!organization || (organization && organization.status !== Organization.status.inactive)) &&
      organization?.isEnabledCoreFeatures,
    ref: React.createRef(),
  },
  team: {
    name: 'My team',
    nameKey: 'navigation.myTeam',
    slug: 'team',
    paths: ['/team'],
    label: 'My team',
    icon: <TeamIcon />,
    hasAccess: (user: any, organization: any) =>
      user &&
      !user.isMyKlayoUser &&
      user.isFullAccess &&
      (user.isTeamLead || user.isOrganizationAdmin) &&
      (!organization || (organization && organization.status !== Organization.status.inactive)) &&
      organization?.isEnabledCoreFeatures,
    admin: true,
    ref: React.createRef(),
  },
  company: {
    name: 'My company',
    nameKey: 'navigation.myCompany',
    slug: 'company',
    paths: ['/company'],
    label: 'My company',
    icon: <CompanyIcon />,
    hasAccess: (user: any, organization: any) =>
      user && !user.isMyKlayoUser && user.isFullAccess && organization?.isEnabledCoreFeatures,
    showInNav: true,
    ref: React.createRef(),
  },
  groups: {
    name: 'My groups',
    nameKey: 'navigation.myGroups',
    slug: 'groups',
    paths: ['/groups'],
    label: 'My groups',
    icon: <CompanyIcon />,
    hasAccess: (user: any, organization: any) =>
      user &&
      !user.isMyKlayoUser &&
      user.isFullAccess &&
      user.hasGroup &&
      organization?.isEnabledCoreFeatures,
    hasChildren: true,
    showInNav: true,
    ref: React.createRef(),
  },
  practicals: {
    name: 'Practicals',
    nameKey: 'navigation.practicals',
    slug: 'practicals',
    paths: ['/practicals', '/practicals/new'],
    label: 'Practicals',
    icon: <CompanyIcon />,
    hasAccess: (user: any, organization: any) =>
      user &&
      !user.isMyKlayoUser &&
      user.isFullAccess &&
      user.isInstructor &&
      !!organization?.enabledFeatures?.Training,
    showInNav: true,
    ref: React.createRef(),
  },
  feedback: {
    name: 'My requests',
    nameKey: 'navigation.myRequests',
    slug: 'feedback',
    paths: ['/feedback'],
    label: 'My requests',
    icon: <FeedbackIcon />,
    hasAccess: (user: any, organization: any) =>
      user &&
      !user.isMyKlayoUser &&
      (user.isTrainingManager ||
        ((user.isTeamLead || user.hasGroup || user.isOrganizationAdmin) &&
          (organization?.isEnabledCoreFeatures || !!organization?.enabledFeatures?.Training))) &&
      (!organization || (organization && organization.status !== Organization.status.inactive)),
    admin: true,
    ref: React.createRef(),
  },
  settings: {
    name: 'Manage',
    nameKey: 'navigation.manage',
    slug: 'settings',
    paths: ['/settings'],
    label: 'Settings',
    // ROUTE access: open the /settings tree to ANY logged-in user so the Job Builder first-job flow
    // (→ /settings/jobs/new) works for everyone. Only the Jobs sub-page actually opens for
    // non-admins — every other Manage sub-page keeps its own admin/role gate, so nothing else leaks.
    hasAccess: (user: any) => !!user,
    // NAV visibility: the Manage item stays hidden for non-admins until they own a personal job, then
    // appears so they can get back to it (entry before that is via Job Builder).
    isVisibleInNav: (user: any) =>
      user &&
      (user.isOrganizationAdmin ||
        user.isBillingUser ||
        user.isTrainingManager ||
        user.hasPersonalJobs),
    hasChildren: true,
    admin: true,
    showInNav: true,
    seperate: true,
    ref: React.createRef(),
  },

  onboarding: {
    name: 'Onboarding',
    nameKey: 'navigation.onboarding',
    slug: 'onboarding',
    paths: ['/onboarding'],
    label: 'Onboarding',
    hasAccess: (user: any) => true,
    admin: false,
    showInNav: false,
    ref: React.createRef(),
  },
  signup: {
    name: 'Sign up',
    nameKey: 'navigation.signUp',
    slug: 'signup',
    paths: ['/signup'],
    label: 'Sign up',
    hasAccess: (user: any) => true,
    admin: true,
    showInNav: false,
    ref: React.createRef(),
  },
  'pricing-and-checkout': {
    name: 'Pricing',
    nameKey: 'navigation.pricing',
    slug: 'pricing-and-checkout',
    paths: ['/pricing-and-checkout'],
    label: 'Pricing',
    hasAccess: (user: any) => true,
    admin: false,
    showInNav: false,
    ref: React.createRef(),
  },
  'solution-finder': {
    name: 'Solution Finder',
    nameKey: 'navigation.solutionFinder',
    slug: 'solution-finder',
    paths: ['/solution-finder'],
    label: 'Solution Finder',
    hasAccess: (_user: any, organization: any) => true,
    admin: true,
    showInNav: false,
    ref: React.createRef(),
  },
  'job-benchmarking': {
    name: 'Job Benchmarking',
    nameKey: 'navigation.jobBenchmarking',
    slug: 'job-benchmarking',
    paths: ['/job-benchmarking'],
    label: 'Job Benchmarking',
    hasAccess: (user: any, organization: any) => true,
    admin: false,
    showInNav: false,
    ref: React.createRef(),
  },
  'job-builder': {
    name: 'Job Builder',
    nameKey: 'navigation.jobBuilder',
    slug: 'job-builder',
    paths: ['/job-builder'],
    label: 'Job Builder',
    hasAccess: (user: any) => canUseJobBuilder(user),
    admin: false,
    showInNav: false,
    ref: React.createRef(),
  },
  try: {
    name: 'Try It Now',
    nameKey: 'solutionFinder.tryItNow',
    slug: 'try',
    paths: ['/try'],
    label: 'Try It Now',
    // Primary arming gate: the preview sandbox is only for trial/free orgs, so the route is
    // never even registered for a paying org. TryPage repeats this check as defence-in-depth.
    hasAccess: (_user: any, organization: any) => !!organization?.isTrial,
    admin: false,
    showInNav: false,
    ref: React.createRef(),
  },
  'my-account': {
    name: 'My account',
    nameKey: 'myAccount.title',
    slug: 'my-account',
    paths: ['/my-account'],
    label: 'My account',
    // Route-only: reachable from the account menu, not shown in the left nav.
    hasAccess: (user: any) => !!user,
    admin: false,
    showInNav: false,
    ref: React.createRef(),
  },
};
